import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { MapPin, Phone } from 'lucide-react'
import BrandMark from '@/components/generals/brand-mark'
import WaveLines from '@/components/generals/wave-lines'
import { directionsUrl, siteConfig, storeInfo } from '@/constants/site'

const shopLinks = [
    { label: "All gear", href: '/shop' },
    { label: "Your cart", href: '/cart' },
    { label: "How pickup works", href: '/#pickup' },
    { label: "Repairs and servicing", href: '/#services' }
]

const SiteFooter: FC = (): ReactNode => {
    return (
        <footer className='relative overflow-hidden bg-deep text-deep-foreground'>
            <WaveLines className='absolute inset-x-0 top-0 h-40 w-full text-lagoon/10' lines={6} />

            <div className='relative mx-auto grid w-full max-w-7xl gap-12 px-5 pt-16 pb-12 sm:px-8 md:grid-cols-12'>
                <div className='md:col-span-4'>
                    <Link href='/' className='inline-flex items-center gap-2.5'>
                        <BrandMark />

                        <span className='font-heading text-xl font-extrabold tracking-tight'>
                            {siteConfig.name}
                        </span>
                    </Link>

                    <p className='mt-5 max-w-xs text-sm leading-relaxed text-deep-foreground/70'>
                        A local tackle shop for anglers who fish rivers, lakes and the coast. Reserve your gear online and pick it up on the way to the water.
                    </p>
                </div>

                <nav aria-label='Footer' className='md:col-span-2'>
                    <p className='text-xs font-bold tracking-[0.18em] text-accent uppercase'>
                        Shop
                    </p>

                    <ul className='mt-4 space-y-2.5 text-sm'>
                        {shopLinks.map(link => (
                            <li key={link.href}>
                                <Link
                                    href={link.href}
                                    className='text-deep-foreground/80 transition-colors hover:text-deep-foreground'
                                >
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='md:col-span-3'>
                    <p className='text-xs font-bold tracking-[0.18em] text-accent uppercase'>
                        Visit
                    </p>

                    <address className='mt-4 space-y-3 text-sm not-italic'>
                        <a
                            href={directionsUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='flex items-start gap-2.5 text-deep-foreground/80 transition-colors hover:text-deep-foreground'
                        >
                            <MapPin className='mt-0.5 size-4 shrink-0 text-lagoon' />

                            <span>
                                {storeInfo.street}
                                <br />
                                {storeInfo.city}
                            </span>
                        </a>

                        <a
                            href={storeInfo.phoneHref}
                            className='flex items-center gap-2.5 text-deep-foreground/80 transition-colors hover:text-deep-foreground'
                        >
                            <Phone className='size-4 shrink-0 text-lagoon' />
                            {storeInfo.phone}
                        </a>

                        <Link
                            href='/contact'
                            className='inline-block pt-1 font-semibold text-accent underline-offset-4 hover:underline'
                        >
                            Send us a message
                        </Link>
                    </address>
                </div>

                <div className='md:col-span-3'>
                    <p className='text-xs font-bold tracking-[0.18em] text-accent uppercase'>
                        Opening hours
                    </p>

                    <dl className='mt-4 space-y-2.5 text-sm'>
                        {storeInfo.hours.map(entry => (
                            <div key={entry.days}>
                                <dt className='text-deep-foreground/60'>
                                    {entry.days}
                                </dt>

                                <dd className='font-medium'>
                                    {entry.time}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>
            </div>

            <div className='relative border-t border-white/10'>
                <div className='mx-auto flex w-full max-w-7xl flex-col gap-2 px-5 py-6 text-xs text-deep-foreground/60 sm:flex-row sm:items-center sm:justify-between sm:px-8'>
                    <p>
                        &copy; <span suppressHydrationWarning>{new Date().getFullYear()}</span> {siteConfig.name}. All prices in US dollars.
                    </p>

                    <p>
                        No payment online. Pay at the counter when you collect.
                    </p>
                </div>
            </div>
        </footer>
    )
}

export default SiteFooter
